import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { LoginComponent } from './login/login/login.component';
import { authBypassGuard } from './security/guards/auth-bypass.guard';
import { PasswordRecoveryPageComponent } from './login/password-recovery-page/password-recovery-page.component';
import {
  PasswordRecoveryConfirmationComponent
} from './login/password-recovery-confirmation/password-recovery-confirmation.component';
import { PasswordChangeComponent } from './login/password-change/password-change.component';
import { RegistrationComponent } from './login/registration/registration.component';

import { LanguageComponent } from './translations/language/language.component';

import { OrganisationFormManagementComponent } from './form/pages/organisation-form-management/organisation-form-management.component';
import { repositoryGuard } from './security/guards/repository.guard';
import { contractEditorGuard } from './security/guards/contract-editor.guard';
import { TermsConditionsComponent } from './utils/pages/policies/terms-conditions/terms-conditions.component';
import { PrivacyPolicyComponent } from './utils/pages/policies/privacy-policy/privacy-policy.component';
import { LegalNoticeComponent } from './utils/pages/policies/legal-notice/legal-notice.component';
import { DataProcessingAgreementComponent } from './utils/pages/policies/data-processing-agreement/data-processing-agreement.component';
import { DocumentationComponent } from './utils/pages/policies/documentation/documentation.component';
import { ReleaseNotesComponent } from './utils/pages/policies/release-notes/release-notes.component';

const routes: Routes = [
  {
    path: '',
    children: [
      { path: '', redirectTo: '/login', pathMatch: 'full' },
      { path: 'login', component: LoginComponent, canActivate: [authBypassGuard()] },
      { path: 'account/password-recovery', component: PasswordRecoveryPageComponent },
      { path: 'account/password-recovery-confirmation', component: PasswordRecoveryConfirmationComponent },
      { path: 'account/password-change', component: PasswordChangeComponent },
      { path: 'account/register', component: RegistrationComponent },
      {
        path: 'dashboard',
        loadChildren: () => import('./home/home.module').then(m => m.HomeModule),
      },
      { path: 'language', component: LanguageComponent },
      {
        path: 'repository',
        loadChildren: () => import('./contract-repository/repository.module').then(m => m.RepositoryModule)
      },
      {
        path: 'external/repository',
        loadChildren: () => import('./contract-repository/repository-external.module').then(m => m.RepositoryExternalModule)
      },
      {
        path: 'register',
        loadChildren: () => import('./regulatory-register/register/register.module').then(m => m.RegisterModule)
      },
      {
        path: 'register/setup',
        loadChildren: () => import('./regulatory-register/register-setup/register-setup.module').then(m => m.RegisterSetupModule)
      },
      {
        path: 'register/import/dora',
        loadChildren: () => import('./register-import/dora-import/dora-import.module').then(m => m.DoraImportModule),
        canActivate: [repositoryGuard]
      },
      {
        path: 'register/import/gdpr',
        loadChildren: () => import('./register-import/gdpr-import/gdpr-import.module').then(m => m.GdprImportModule),
        canActivate: [repositoryGuard]
      },
      {
        path: 'register/import/outsourcing',
        loadChildren: () => import('./register-import/outsourcing-import/outsourcing-import.module').then(m => m.OutsourcingImportModule),
        canActivate: [repositoryGuard]
      },
      {
        path: 'regulatory/notification',
        loadChildren: () => import('./regulatory-notification/regulatory-notification.module').then(m => m.RegulatoryNotificationModule)
      },

      {
        path: 'entities',
        loadChildren: () => import('./entity/entity.module').then(m => m.EntityModule)
      },
      {
        path: 'keydata',
        loadChildren: () => import('./key-data/key-data.module').then(m => m.KeyDataModule)
      },
      {
        path: 'keydataset',
        loadChildren: () => import('./key-data-set/key-data-set.module').then(m => m.KeyDataSetModule)
      },
      {
        path: 'pending',
        loadChildren: () => import('./pending/pending.module').then(m => m.PendingModule)
      },

      {
        path: 'template',
        loadChildren: () => import('./template/template.module').then(m => m.TemplateModule)
      },
      {
        path: 'contract/editor/:uuid',
        loadChildren: () => import('./contract-editor/contract-editor-routing.module').then(m => m.ContractEditorRoutingModule)
      },
      {
        path: 'external/contract/editor/:uuid',
        loadChildren: () => import('./contract-editor/contract-editor-external-routing.module').then(m => m.ContractEditorExternalRoutingModule)
      },
      {
        path: 'contract/storage/:uuid',
        loadChildren: () => import('./contract-storage/contract-storage.module').then(m => m.ContractStorageModule)
      },
      {
        path: 'contract/hierarchy/:uuid',
        loadChildren: () => import('./contract-hierarchy/contract-hierarchy.module').then(m => m.ContractHierarchyModule)
      },
      {
        path: 'contract/keydata/extraction/:uuid',
        loadComponent: () => import('./contract_keydata_extraction/pages/contract-keydata-extraction-evaluation-page/contract-keydata-extraction-evaluation-page.component').then(m => m.ContractKeydataExtractionEvaluationPageComponent),
        canActivate: [contractEditorGuard]
      },
      {
        path: 'contract/list',
        loadChildren: () => import('./contract/contract.module').then(m => m.ContractModule)
      },
      {
        path: 'contract-list',
        redirectTo: 'contract/list',
        pathMatch: 'full'
      },
      {
        path: 'contract/create',
        loadChildren: () => import('./contract/contract.module').then(m => m.ContractModule)
      },
      {
        path: 'contract/:uuid',
        loadChildren: () => import('./contract-detail/contract-detail-routing.module').then(m => m.ContractDetailRoutingModule)
      },
      {
        path: 'clause-upload',
        loadChildren: () => import('./clause/clause.module').then(m => m.ClauseModule)
      },

      {
        path: 'corporate',
        loadChildren: () => import('./corporate-structure/corporate-structure.module').then(m => m.CorporateStructureModule)
      },

      {
        path: 'settings',
        loadChildren: () => import('./settings/settings.module').then(m => m.SettingsModule)
      },
      {
        path: 'settings/notification',
        loadChildren: () => import('./notification-settings/notification-settings.module').then(m => m.NotificationSettingsModule)
      },

      {
        path: 'organisation/users',
        loadChildren: () => import('./users/users.module').then(m => m.UsersModule)
      },
      {
        path: 'organisation/teams',
        loadChildren: () => import('./team/team.module').then(m => m.TeamModule)
      },
      { path: 'organisation/form', component: OrganisationFormManagementComponent, canActivate: [repositoryGuard]},
      {
        path: 'organisation/api',
        loadChildren: () => import('./api-connection/api.module').then(m => m.ApiModule)
      },
      {
        path: 'organisation',
        loadChildren: () => import('./organisation/organisation.module').then(m => m.OrganisationModule)
      },

      {
        path: 'notification/list',
        loadChildren: () => import('./notification-list/notification-list.module').then(m => m.NotificationListModule)
      },

      {
        path: 'matrix',
        loadChildren: () => import('./matrix/matrix.module').then(m => m.MatrixModule),
        canActivate: [repositoryGuard]
      },


      {
        path: 'dev-zone',
        loadComponent: () => import('./dev-zone/page/dev-zone/dev-zone.component').then(m => m.DevZoneComponent)
      },

      { path: 'terms-conditions', component: TermsConditionsComponent },
      { path: 'privacy-policy', component: PrivacyPolicyComponent },
      { path: 'legal-notice', component: LegalNoticeComponent },
      { path: 'data-processing-agreement', component: DataProcessingAgreementComponent },
      { path: 'documentation', component: DocumentationComponent },
      { path: 'documentations', redirectTo: 'documentation', pathMatch: 'full' },
      { path: 'release-notes', component: ReleaseNotesComponent },

    ],
  },
  { path: '**', redirectTo: '/login' }
];

@NgModule({
  imports: [RouterModule.forRoot(routes, { useHash: true })],
  exports: [RouterModule]
})
export class AppRoutingModule { }
